import { useSchedulerContext } from '@contexts/scheduler';
import { SchedulerGrid } from './grid';
import { SchedulerProps } from './types';
import { TouchEvent, useMemo, useRef } from 'react';
import { useSchedulerSync } from '@hooks/scheduler/sync';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Modifier,
} from '@dnd-kit/core';
import { getSegmentFromPoint } from '@hooks/scheduler/dnd/helpers';
import { restrictToBoundingRect } from '@utils/dnd/restrictToBoundingRect';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

export default function Scheduler({
  date,
  foremen,
  withoutTeam,
  outOfScopeRecords,
  modal,
  inventory,
  schedule,
  activeId,
  restrictTo = 'window',
}: SchedulerProps) {
  const { setState } = useSchedulerContext();

  useSchedulerSync({
    date,
    foremen,
    withoutTeam,
    outOfScopeRecords,
    modal,
    inventory,
    schedule,
    activeId,
    prefillShift: !inventory,
  });

  const mouseSensor = useSensor(MouseSensor, {
    onActivation: ({ event }) => {
      const { clientX, clientY } = event as unknown as MouseEvent;

      setState((prev) => ({
        ...prev,
        active: {
          ...prev.active,
          newValues: { ...prev.active.newValues, from: getSegmentFromPoint(clientX, clientY) },
        },
      }));
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    onActivation: ({ event }) => {
      const { touches } = event as unknown as TouchEvent;
      const { clientX, clientY } = touches[0];

      setState((prev) => ({
        ...prev,
        active: {
          ...prev.active,
          newValues: { ...prev.active.newValues, from: getSegmentFromPoint(clientX, clientY) },
        },
      }));
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const gridBoundsRef = useRef<HTMLDivElement | null>(null);
  const restrictToSchedulerGrid = useMemo<Modifier>(
    () =>
      ({ transform, draggingNodeRect }) => {
        const el = gridBoundsRef.current;
        if (!draggingNodeRect || !el) return transform;
        return restrictToBoundingRect(transform, draggingNodeRect, el.getBoundingClientRect());
      },
    [],
  );

  const modifier = restrictTo === 'window' ? restrictToWindowEdges : restrictToSchedulerGrid;
  return (
    <DndContext sensors={sensors} modifiers={[modifier]}>
      <SchedulerGrid boundsRef={gridBoundsRef} />
    </DndContext>
  );
}
